<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { Filters } from '../types';
import useMainTableFilters from './useMainTableFilters';
import { useLocale } from '../../../composables/useLocale';
import EnumFilter from './EnumFilter.vue';
import BooleanFilter from './BooleanFilter.vue';
import { toRaw } from 'vue';

const { t } = useLocale();

const props = defineProps<{ filters: Filters }>();
const { filters: filtersFromQuery, setFilters, clearFilters } = useMainTableFilters();

const filterValues = ref<Record<string, any>>({});

watch(
  () => props.filters,
  (filters) => {
    nextTick(() => {
      Object.keys(filters).forEach((key) => {
        filterValues.value[key] = undefined;
      });
    });
  },
  { deep: true, immediate: true },
);
watch(
  () => filtersFromQuery.value,
  (newFilters) => {
    nextTick(() => {
      Object.keys(props.filters).forEach((key) => {
        if (newFilters && key in newFilters) {
          filterValues.value[key] = newFilters[key];
        }
      });
    });
  },
  { deep: true, immediate: true },
);

const clearAllFilters = () => {
  Object.keys(props.filters).forEach((key) => {
    filterValues.value[key] = undefined;
  });
  clearFilters();
};

const resetFilters = () => {
  const resetValues: Record<string, any> = {};
  Object.keys(props.filters).forEach((key) => {
    resetValues[key] = props.filters[key]?.default !== undefined ? props.filters[key]?.default : undefined;
  });

  Object.assign(filterValues.value, resetValues);

  const changedFilters: Record<string, any> = {};
  Object.keys(resetValues).forEach((key) => {
    if (JSON.stringify(filtersFromQuery.value?.[key]) !== JSON.stringify(resetValues[key])) {
      if (resetValues[key] !== undefined) {
        changedFilters[key] = resetValues[key];
      }
    }
  });

  if (Object.keys(changedFilters).length === 0) {
    clearFilters();
  } else {
    setFilters(changedFilters);
  }
};

const filtersChanged = computed(() => {
  return Object.keys(filterValues.value).some((key) => {
    return JSON.stringify(filterValues.value[key]) !== JSON.stringify(filtersFromQuery.value?.[key]);
  });
});

const filtersEmpty = computed(() => {
  return Object.keys(filterValues.value).every((key) => {
    const value = filterValues.value[key];
    return value === undefined || value === null || (Array.isArray(value) && value.length === 0);
  });
});

const filtersWithOnlyDefaults = computed(() => {
  return Object.keys(filterValues.value).every((key) => {
    const filter = props.filters[key];
    const defaultValue = filter?.default;
    return JSON.stringify(filterValues.value[key]) === JSON.stringify(defaultValue);
  });
});

// make all expansion panels open always
const openFiltersPanelModal = computed(() => Object.keys(props.filters).map((_i, idx) => idx));

const applyFilters = () => {
  if (!filtersChanged.value) {
    clearFilters();
  } else {
    setFilters(structuredClone(toRaw(filterValues.value)));
  }
};

defineExpose({
  filtersChanged,
  filtersEmpty,
  filtersWithOnlyDefaults,
  applyFilters,
  clearAllFilters,
  resetFilters,
  isResetFiltersEnabled: false,
});
</script>

<template>
  <v-expansion-panels
    v-model="openFiltersPanelModal"
    variant="accordion"
    multiple
    expand-icon=""
    collapse-icon=""
    disable-icon-rotate
    elevation="0"
  >
    <v-expansion-panel v-for="(filter, FilterName) in filters" :key="`expansion-${String(FilterName)}`">
      <v-expansion-panel-title static style="cursor: default; opacity: 1" class="pa-0">
        {{ filter?.labelTransKey ? t(filter.labelTransKey) : t(`lcp.${String(FilterName)}`) }}
      </v-expansion-panel-title>
      <v-expansion-panel-text>
        <template v-if="filter && filter.type === 'enum'">
          <EnumFilter :name="String(FilterName)" :filter="filter" v-model="filterValues[FilterName]" />
        </template>
        <template v-else-if="filter && filter.type === 'boolean'">
          <BooleanFilter :name="String(FilterName)" :filter="filter" v-model="filterValues[FilterName]" />
        </template>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<style scoped lang="scss">
.v-expansion-panel-title {
  --v-hover-opacity: 0;
}
:deep(.v-expansion-panel-text__wrapper) {
  padding: 0 !important;
}
</style>
